import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  type Theme,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  InfoOutlined as InfoIcon,
  Inventory2 as SupplyIcon,
  Save as SaveIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import { useSelfSetupTour } from '../hooks/useSelfSetupTour';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationType, type OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { useOrganizationWasteTypes } from '../hooks/useOrganizationWasteTypes';
import { APPETITE_WRITE_ROLES, PERM_APPETITE_WRITE } from '../auth';
import {
  HELP_GUIDE_SUPPLY,
  HELP_GUIDE_SUPPLY_ACCEPTS_EXTRA,
  HELP_GUIDE_SUPPLY_LINES,
} from '../constants/knowledgeBaseTryIt';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import { getOrganization, patchRecyclerAppetite } from '../services/organization.service';
import { getApiErrorDetail } from '../helpers/api-error.helper';
import {
  RECYCLER_APPETITE_MASS_UNITS,
  isMassUnit,
  type RecyclerAppetiteRepeat,
  type RecyclerAppetiteStored,
} from '../types/recycler-appetite.type';

interface GenericResponse<T> {
  result: T;
}

interface LineFormRow {
  key: string;
  material_id: string;
  parent_category: string;
  quantity: number;
  unit: string;
}

const newEmptyLine = (): LineFormRow => ({
  key: crypto.randomUUID(),
  material_id: '',
  parent_category: '',
  quantity: 1,
  unit: 'kg',
});

const appetiteToRows = (appetite: RecyclerAppetiteStored | undefined | null): LineFormRow[] => {
  const lines = appetite?.lines;
  if (!lines?.length) return [newEmptyLine()];
  return lines.map((l) => ({
    key: crypto.randomUUID(),
    material_id: l.material_id,
    parent_category: l.parent_category,
    quantity: l.quantity,
    unit: l.unit,
  }));
};

/** What is compared and sent: the rows without their client-side keys. */
const serializeForm = (rows: LineFormRow[], repeat: RecyclerAppetiteRepeat, acceptsExtra: boolean) =>
  JSON.stringify({
    repeat,
    accepts_additional_quantity: acceptsExtra,
    lines: rows.map((r) => ({
      material_id: r.material_id,
      parent_category: r.parent_category,
      quantity: r.quantity,
      unit: r.unit,
    })),
  });

export function getSaveBlockReason(
  rows: LineFormRow[],
  opts: {
    orgLoading: boolean;
    canWrite: boolean;
    hasMaterials: boolean;
    materialsLoading: boolean;
    isInBasket: (materialTypeId: string) => boolean;
    t: (key: string) => string;
  },
): string | null {
  const { orgLoading, canWrite, hasMaterials, materialsLoading, isInBasket, t } = opts;
  if (orgLoading || materialsLoading) return t('supply.saveBlockedLoading');
  if (!canWrite) return t('supply.saveBlockedNoPermission');
  if (!hasMaterials) return t('supply.saveBlockedNoMaterials');
  const seen = new Set<string>();
  for (const r of rows) {
    if (!r.material_id) return t('supply.saveBlockedSelectWasteType');
    if (!isInBasket(r.material_id)) return t('supply.saveBlockedNotInBasket');
    if (seen.has(r.material_id)) return t('supply.saveBlockedDuplicate');
    seen.add(r.material_id);
    if (!(r.quantity > 0)) return t('supply.saveBlockedEnterQuantity');
    if (!isMassUnit(r.unit)) return t('supply.saveBlockedLegacyUnit');
  }
  return null;
}

const toggleButtonSx = (theme: Theme) => ({
  gap: 1,
  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
    marginLeft: 0,
  },
  '& .MuiToggleButton-root': {
    px: 2.5,
    py: 0.75,
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '8px !important',
    border: `1px solid ${theme.palette.divider} !important`,
    '&.Mui-selected': {
      bgcolor: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
});

const Supply: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const selectedOrganization = useOrganizationStore((s) => s.selectedOrganization);
  const { canMutate, orgId: selectedOrgId } = useAuthPermissions();
  const canWriteAppetite = selectedOrgId
    ? canMutate(PERM_APPETITE_WRITE(selectedOrgId), APPETITE_WRITE_ROLES)
    : false;

  const orgId = selectedOrganization?.id;
  const isRecycler = selectedOrganization?.organization_type === OrganizationType.RECYCLER;

  const {
    rows: basketRows,
    byMaterialTypeId,
    groupedByCategory,
    isLoading: materialTypesLoading,
  } = useOrganizationWasteTypes(orgId, isRecycler);

  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [rows, setRows] = useState<LineFormRow[]>([newEmptyLine()]);
  const [repeat, setRepeat] = useState<RecyclerAppetiteRepeat>('weekly');
  const [acceptsExtra, setAcceptsExtra] = useState(true);
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOkOpen, setSaveOkOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    isStep,
    tourPhase,
    advanceTourPhase,
    completeStep,
    dismissTour,
  } = useSelfSetupTour();
  const [helpSupplyGuide, setHelpSupplyGuide] = useState(false);
  const [helpSupplyPhase, setHelpSupplyPhase] = useState<
    'period' | 'lines' | 'acceptsExtra' | 'save'
  >('period');
  /** When set, help guide shows a single tip then Got it. */
  const [helpSupplySinglePhase, setHelpSupplySinglePhase] = useState<
    'lines' | 'acceptsExtra' | null
  >(null);

  const finishHelpSupplyGuide = useCallback(() => {
    setHelpSupplyGuide(false);
    setHelpSupplyPhase('period');
    setHelpSupplySinglePhase(null);
    clearHelpGuideActive();
  }, []);

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (guide === HELP_GUIDE_SUPPLY_LINES) {
      markHelpGuideActive(guide);
      setHelpSupplyGuide(true);
      setHelpSupplySinglePhase('lines');
      setHelpSupplyPhase('lines');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }
    if (guide === HELP_GUIDE_SUPPLY_ACCEPTS_EXTRA) {
      markHelpGuideActive(guide);
      setHelpSupplyGuide(true);
      setHelpSupplySinglePhase('acceptsExtra');
      setHelpSupplyPhase('acceptsExtra');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }
    if (guide !== HELP_GUIDE_SUPPLY) return;
    markHelpGuideActive(guide);
    setHelpSupplyGuide(true);
    setHelpSupplySinglePhase(null);
    setHelpSupplyPhase('period');
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  const loadOrg = useCallback(async () => {
    if (!orgId || !isRecycler) return;
    setOrgLoading(true);
    setOrgError(null);
    try {
      const res = (await getOrganization(orgId)) as GenericResponse<OrganizationSchema>;
      const o = res.result;
      const appetite = o.data?.recycler_appetite as RecyclerAppetiteStored | undefined;
      const nextAccepts = appetite?.accepts_additional_quantity !== false;
      const nextRepeat: RecyclerAppetiteRepeat = appetite?.repeat === 'monthly' ? 'monthly' : 'weekly';
      const nextRows = appetiteToRows(appetite);
      setAcceptsExtra(nextAccepts);
      setRepeat(nextRepeat);
      setRows(nextRows);
      setSavedSnapshot(serializeForm(nextRows, nextRepeat, nextAccepts));
    } catch {
      setOrgError('load');
    } finally {
      setOrgLoading(false);
    }
  }, [orgId, isRecycler]);

  useEffect(() => {
    void loadOrg();
  }, [loadOrg]);

  const isDirty = savedSnapshot !== '' && serializeForm(rows, repeat, acceptsExtra) !== savedSnapshot;

  // The app uses a plain BrowserRouter, so in-app navigation cannot be blocked;
  // the browser prompt still covers reloads and closed tabs.
  useEffect(() => {
    if (!isDirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  const configuredLineCount = useMemo(
    () => rows.filter((r) => r.parent_category && r.material_id && r.quantity > 0).length,
    [rows],
  );

  const hasMaterials = basketRows.some((row) => Boolean(row.material_type_id));
  const noMaterials = !materialTypesLoading && !orgLoading && !hasMaterials;
  const isInBasket = useCallback(
    (materialTypeId: string) => byMaterialTypeId.has(materialTypeId),
    [byMaterialTypeId],
  );

  const chosenElsewhere = (row: LineFormRow, materialTypeId: string) =>
    rows.some((other) => other.key !== row.key && other.material_id === materialTypeId);

  const updateRow = (key: string, patch: Partial<LineFormRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const onMaterialChange = (key: string, materialTypeId: string) => {
    if (!materialTypeId) {
      updateRow(key, { parent_category: '', material_id: '' });
      return;
    }
    const material = byMaterialTypeId.get(materialTypeId);
    if (!material) return;
    updateRow(key, {
      material_id: materialTypeId,
      parent_category: material.category_code ?? material.parent_category ?? '',
    });
  };

  const addRow = () => setRows((prev) => [...prev, newEmptyLine()]);
  const removeRow = (key: string) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));

  const saveBlockReason = getSaveBlockReason(rows, {
    orgLoading,
    canWrite: canWriteAppetite,
    hasMaterials,
    materialsLoading: materialTypesLoading,
    isInBasket,
    t,
  });
  const validate = (): boolean => saveBlockReason === null;

  const handleSave = async () => {
    if (!orgId || !validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      await patchRecyclerAppetite(orgId, {
        accepts_additional_quantity: acceptsExtra,
        repeat,
        lines: rows.map((r) => ({
          material_id: r.material_id,
          parent_category: r.parent_category,
          quantity: r.quantity,
          unit: r.unit,
        })),
      });
      setSaveOkOpen(true);
      await loadOrg();
    } catch (e: unknown) {
      setSaveError(getApiErrorDetail(e) ?? t('supply.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (!isRecycler) return null;

  const quantityLabel = repeat === 'monthly' ? t('supply.quantityPerMonth') : t('supply.quantityPerWeek');
  const periodHelp = repeat === 'monthly' ? t('supply.repeatHelpMonthly') : t('supply.repeatHelpWeekly');
  const canSave = validate() && !saving && isDirty;

  const showSupplyTour = isStep('supply') && tourPhase !== 'nav' && !helpSupplyGuide;
  const showPeriodTip =
    (showSupplyTour && tourPhase === 'period') ||
    (helpSupplyGuide && helpSupplyPhase === 'period');
  const showLinesTip =
    (showSupplyTour && tourPhase === 'lines') ||
    (helpSupplyGuide && helpSupplyPhase === 'lines');
  const showAcceptsExtraTip =
    (showSupplyTour && tourPhase === 'acceptsExtra') ||
    (helpSupplyGuide && helpSupplyPhase === 'acceptsExtra');
  const showSaveTip =
    (showSupplyTour && tourPhase === 'save') ||
    (helpSupplyGuide && helpSupplyPhase === 'save');

  const softDismissSupplyTour = () => {
    if (helpSupplyGuide) {
      finishHelpSupplyGuide();
      return;
    }
    dismissTour();
  };

  const finishSupplyTour = () => completeStep('supply');

  const advanceSupplyTip = () => {
    if (helpSupplyGuide) {
      if (helpSupplySinglePhase || helpSupplyPhase === 'save') {
        finishHelpSupplyGuide();
        return;
      }
      if (helpSupplyPhase === 'period') {
        setHelpSupplyPhase('lines');
        return;
      }
      if (helpSupplyPhase === 'lines') {
        setHelpSupplyPhase('acceptsExtra');
        return;
      }
      if (helpSupplyPhase === 'acceptsExtra') {
        setHelpSupplyPhase('save');
        return;
      }
      finishHelpSupplyGuide();
      return;
    }
    if (tourPhase === 'save') {
      finishSupplyTour();
      return;
    }
    advanceTourPhase();
  };

  const saveButton = (
    <SetupCoachmark
      open={showSaveTip}
      title={t('selfSetup.tour.supplySaveTitle')}
      subtitle={t('selfSetup.tour.supplySaveSubtitle')}
      onDismiss={softDismissSupplyTour}
      onNext={advanceSupplyTip}
      nextLabel={t('selfSetup.tourDone')}
    >
      <Button
        id="self-setup-supply-save"
        variant="contained"
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        onClick={() => void handleSave()}
        disabled={!canSave}
        sx={{ borderRadius: '12px', px: 3, py: 1.5, flexShrink: 0 }}
      >
        {saving ? t('supply.saving') : t('supply.save')}
      </Button>
    </SetupCoachmark>
  );

  const settingsToolbar = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      justifyContent="space-between"
      gap={2}
      sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 48 }}>
          {t('supply.targetPeriod')}
        </Typography>
        <SetupCoachmark
          open={showPeriodTip}
          title={t('selfSetup.tour.supplyPeriodTitle')}
          subtitle={t('selfSetup.tour.supplyPeriodSubtitle')}
          onDismiss={softDismissSupplyTour}
          onNext={advanceSupplyTip}
          nextLabel={t('selfSetup.tourNext')}
        >
          <ToggleButtonGroup
            id="self-setup-supply-period"
            exclusive
            size="small"
            value={repeat}
            onChange={(_, value: RecyclerAppetiteRepeat | null) => {
              if (value) setRepeat(value);
            }}
            sx={toggleButtonSx(theme)}
          >
            <ToggleButton value="weekly">{t('repeat.weekly')}</ToggleButton>
            <ToggleButton value="monthly">{t('repeat.monthly')}</ToggleButton>
          </ToggleButtonGroup>
        </SetupCoachmark>
        <Tooltip title={periodHelp} arrow placement="top">
          <IconButton size="small" aria-label={t('supply.periodHelpTitle')} sx={{ color: 'text.secondary' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <SetupCoachmark
        open={showAcceptsExtraTip}
        title={t('selfSetup.tour.supplyAcceptsExtraTitle')}
        subtitle={t('selfSetup.tour.supplyAcceptsExtraSubtitle')}
        onDismiss={softDismissSupplyTour}
        onNext={advanceSupplyTip}
        nextLabel={
          helpSupplySinglePhase === 'acceptsExtra'
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
      >
        <Stack
          id="self-setup-supply-accepts-extra"
          direction="row"
          alignItems="center"
          gap={1}
        >
          <Switch
            checked={acceptsExtra}
            onChange={(_, v) => setAcceptsExtra(v)}
            color="primary"
            size="small"
          />
          <Tooltip title={t('supply.acceptsExtraHelp')} arrow placement="top">
            <Typography variant="body2" sx={{ fontWeight: 500, cursor: 'default' }}>
              {t('supply.acceptsExtra')}
            </Typography>
          </Tooltip>
        </Stack>
      </SetupCoachmark>
    </Stack>
  );

  const renderLineRow = (row: LineFormRow) => {
    const missingFromBasket = Boolean(row.material_id) && !isInBasket(row.material_id);
    const legacyUnit = !isMassUnit(row.unit);
    return (
      <TableRow
        key={row.key}
        hover
        sx={{
          '&:last-child td': { borderBottom: 0 },
          '& td': { verticalAlign: 'top', py: 1.75 },
          '& td:first-of-type': { pl: { xs: 2, sm: 2.5 } },
          '& td:last-of-type': { pr: { xs: 2, sm: 2.5 } },
        }}
      >
        <TableCell>
          <FormControl fullWidth size="small" error={missingFromBasket}>
            <InputLabel>{t('supply.wasteType')}</InputLabel>
            <Select
              label={t('supply.wasteType')}
              value={row.material_id}
              onChange={(e) => onMaterialChange(row.key, e.target.value as string)}
              disabled={materialTypesLoading}
            >
              <MenuItem value="">
                <em>{t('supply.selectWasteType')}</em>
              </MenuItem>
              {missingFromBasket && (
                <MenuItem value={row.material_id} disabled>
                  <em>{t('supply.lineNotInBasket')}</em>
                </MenuItem>
              )}
              {groupedByCategory.flatMap(({ category, rows: materials }) => [
                <ListSubheader key={`group-${category}`}>
                  {t(`categories.${category}`, category)}
                </ListSubheader>,
                ...materials
                  .filter((material) => Boolean(material.material_type_id))
                  .map((material) => {
                    const id = material.material_type_id as string;
                    const taken = chosenElsewhere(row, id);
                    return (
                      <MenuItem key={id} value={id} disabled={taken}>
                        {material.name}
                        {taken && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({t('supply.alreadyChosen')})
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  }),
              ])}
            </Select>
          </FormControl>
          {missingFromBasket && (
            <Chip
              size="small"
              color="warning"
              icon={<WarningIcon />}
              label={t('supply.lineNotInBasket')}
              sx={{ mt: 1 }}
            />
          )}
        </TableCell>
        <TableCell>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={quantityLabel}
            value={row.quantity}
            onChange={(e) => updateRow(row.key, { quantity: Number(e.target.value) })}
            inputProps={{ min: 0.0001, step: 'any' }}
          />
        </TableCell>
        <TableCell>
          <FormControl fullWidth size="small" error={legacyUnit}>
            <InputLabel>{t('supply.unit')}</InputLabel>
            <Select
              label={t('supply.unit')}
              value={row.unit}
              onChange={(e) => updateRow(row.key, { unit: e.target.value as string })}
            >
              {legacyUnit && (
                <MenuItem value={row.unit} disabled>
                  {t(`units.${row.unit}`, row.unit)}
                </MenuItem>
              )}
              {RECYCLER_APPETITE_MASS_UNITS.map((u) => (
                <MenuItem key={u} value={u}>
                  {t(`units.${u}`, u)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {legacyUnit && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              {t('supply.unitNotMass')}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Tooltip title={t('supply.removeLine')}>
            <span>
              <IconButton
                size="small"
                onClick={() => removeRow(row.key)}
                disabled={rows.length <= 1}
                sx={{ color: 'text.secondary' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      <PageHeader
        icon={<SupplyIcon />}
        title={t('supply.title')}
        subtitle={t('supply.subtitle')}
        action={
          saveBlockReason && !canSave ? (
            <Tooltip title={saveBlockReason}>
              <span>{saveButton}</span>
            </Tooltip>
          ) : (
            saveButton
          )
        }
      />

      {orgError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('supply.loadError')}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      {isDirty && !saveError && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('supply.unsavedChanges')}
        </Alert>
      )}

      <Snackbar
        open={saveOkOpen}
        autoHideDuration={4000}
        onClose={() => setSaveOkOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSaveOkOpen(false)}
          sx={{ width: '100%' }}
        >
          {t('supply.saved')}
        </Alert>
      </Snackbar>

      {noMaterials ? (
        <Paper elevation={2} sx={{ p: { xs: 3, sm: 5 }, borderRadius: '16px', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t('supply.noMaterialsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: 'auto' }}>
            {t('supply.noMaterialsBody')}
          </Typography>
          <Button
            component={RouterLink}
            to="/materials"
            variant="contained"
            sx={{ borderRadius: '12px' }}
          >
            {t('supply.noMaterialsCta')}
          </Button>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
          {settingsToolbar}

          {orgLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <SetupCoachmark
              open={showLinesTip}
              title={t('selfSetup.tour.supplyLinesTitle')}
              subtitle={t('selfSetup.tour.supplyLinesSubtitle')}
              onDismiss={softDismissSupplyTour}
              onNext={advanceSupplyTip}
              nextLabel={
                helpSupplySinglePhase === 'lines'
                  ? t('selfSetup.tourDone')
                  : t('selfSetup.tourNext')
              }
              fullWidth
            >
              <Box id="self-setup-supply-lines">
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: '42%', pl: { xs: 2, sm: 2.5 } }}>
                          {t('supply.wasteType')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '28%' }}>{quantityLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '22%' }}>{t('supply.unit')}</TableCell>
                        <TableCell align="right" sx={{ width: 56, pr: { xs: 2, sm: 2.5 } }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>{rows.map(renderLineRow)}</TableBody>
                  </Table>
                </TableContainer>

                <Divider />

                <Box
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    id="self-setup-supply-add-line"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    variant="text"
                    sx={{ fontWeight: 600 }}
                  >
                    {t('supply.addLine')}
                  </Button>
                  {configuredLineCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {configuredLineCount} / {rows.length}
                    </Typography>
                  )}
                </Box>
              </Box>
            </SetupCoachmark>
          )}

          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('supply.fulfillmentMarketDisclaimer')}
            </Typography>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default Supply;
